export interface PermissionStatus {
  microphone: boolean;
  notifications: boolean;
  thirdPartyCookies: boolean;
  popups: boolean;
}

export interface PermissionCheckResult {
  allPermissionsGranted: boolean;
  permissions: PermissionStatus;
  errors: string[];
}

/**
 * Check if microphone permission is granted
 */
export const checkMicrophonePermission = async (): Promise<boolean> => {
  try {
    // First check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('getUserMedia is not supported in this browser');
      return false;
    }

    // Check permission state if available
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        return permissionStatus.state === 'granted';
      } catch (error) {
        console.warn('Permission query not supported for microphone, falling back to getUserMedia test');
      }
    }

    // Fallback: Try to access microphone briefly to check permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately stop the stream to avoid keeping microphone active
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return false;
      }
      // Other errors (like NotFoundError for no microphone) should not block the app
      console.warn('Microphone check failed with non-permission error:', error);
      return true; // Assume permission is OK if it's not a permission issue
    }
  } catch (error) {
    console.error('Error checking microphone permission:', error);
    return false;
  }
};

/**
 * Check if notification permission is granted
 */
export const checkNotificationPermission = (): boolean => {
  try {
    if (!('Notification' in window)) {
      console.warn('Notifications are not supported in this browser');
      return false;
    }

    return Notification.permission === 'granted';
  } catch (error) {
    console.error('Error checking notification permission:', error);
    return false;
  }
};

/**
 * Check if popup permission is granted
 */
export const checkPopupPermission = (): boolean => {
  try {
    // Try to open a popup window and immediately close it to test permission
    const testPopup = window.open('', '_blank', 'width=1,height=1');
    if (testPopup) {
      testPopup.close();
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error checking popup permission:', error);
    return false;
  }
};

/**
 * Check all required permissions
 */
export const checkAllPermissions = async (): Promise<PermissionCheckResult> => {
  const errors: string[] = [];

  try {
    const [microphone, notifications, popups] = await Promise.all([
      checkMicrophonePermission().catch(error => {
        errors.push(`Microphone check failed: ${error.message}`);
        return false;
      }),
      Promise.resolve(checkNotificationPermission()).catch(error => {
        errors.push(`Notification check failed: ${error.message}`);
        return false;
      }),
      Promise.resolve(checkPopupPermission()).catch(error => {
        errors.push(`Popup check failed: ${error.message}`);
        return false;
      })
    ]);

    // Third-party cookies are always shown as required but not checked
    // This is just informational for the user
    const thirdPartyCookies = true;

    const permissions: PermissionStatus = {
      microphone,
      notifications,
      popups,
      thirdPartyCookies,
    };

    const allPermissionsGranted = microphone && notifications && thirdPartyCookies && popups;

    return {
      allPermissionsGranted,
      permissions,
      errors
    };
  } catch (error: any) {
    errors.push(`Permission check failed: ${error.message}`);
    return {
      allPermissionsGranted: false,
      permissions: {
        microphone: false,
        notifications: false,
        thirdPartyCookies: true,
        popups: false
      },
      errors
    };
  }
};

/**
 * Get user-friendly permission names for display
 */
export const getPermissionDisplayName = (permission: keyof PermissionStatus): string => {
  const displayNames = {
    microphone: 'Microphone Access',
    notifications: 'Browser Notifications',
    popups: 'Popups and Redirects',
    thirdPartyCookies: 'Third-Party Cookies',
  };

  return displayNames[permission];
};